import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EmoGraphAnimation } from "@/components/landing/EmoGraphAnimation";

const Landing = () => {
  const navigate = useNavigate();

  return (
    <main className="font-sans bg-[#E0F2F1] text-[#034D4D]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 w-full bg-white/80 backdrop-blur-md shadow-sm z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/clou_logo.png" alt="Clou" className="h-8 sm:h-10 w-auto" />
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <a
              href="/split-landing/index.html"
              className="text-sm sm:text-base font-medium text-teal-700 hover:text-teal-900 transition underline underline-offset-4"
            >
              New Split Page
            </a>
            <button
              onClick={() => navigate("/therapist-login")}
              className="text-sm sm:text-base font-medium text-teal-800 hover:text-teal-900 transition"
            >
              Therapist
            </button>
            <button
              onClick={() => navigate("/client-login")}
              className="text-sm sm:text-base font-medium text-teal-800 hover:text-teal-900 transition"
            >
              Client
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#E0F2F1] via-[#B2DFDB] to-[#80CBC4] text-[#034D4D] pt-28 sm:pt-32 md:pt-40 pb-20 sm:pb-28">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.15),transparent_40%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl sm:rounded-[2.2rem] border border-white/40 bg-white/35 backdrop-blur-md shadow-2xl px-6 sm:px-10 md:px-12 py-10 sm:py-14 md:py-16 text-center">
            <motion.h1
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
            >
              Therapy continues <br className="hidden sm:block" />
              between sessions
            </motion.h1>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 1 }}
              className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
            >
              Support your clients in between sessions with structured reflection, mood tracking, and guided journaling — all in a secure, private space.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 1 }}
              className="mt-8 sm:mt-10 flex justify-center gap-3 sm:gap-4 flex-wrap"
            >
              <button
                onClick={() => navigate("/therapist-login")}
                className="bg-teal-400 text-white px-5 sm:px-6 py-3 rounded-2xl shadow hover:scale-105 transition duration-300 font-semibold"
              >
                Join as Therapist
              </button>
              <button
                onClick={() => navigate("/client-login")}
                className="bg-white/10 backdrop-blur px-5 sm:px-6 py-3 rounded-2xl font-medium border border-white/30 hover:bg-white/20 transition"
              >
                Join as Client
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features split — text and dashboard side by side */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-20 sm:space-y-24">
          {/* For Clients: text left, dashboard right */}
          <motion.div
            initial={{ x: -30, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl shadow-2xl bg-gradient-to-br from-[#F1F8F7] to-[#E0F2F1] min-h-[360px] md:min-h-[520px] p-4 sm:p-5"
          >
            <div className="rounded-3xl overflow-hidden">
              <img
                src="/client_dashboard.png"
                alt="Client dashboard — journaling, mood tracking, and reflections"
                className="w-full h-full max-h-[620px] object-contain"
              />
            </div>
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-0 md:right-0 md:translate-x-1/3 -translate-y-1/4 max-w-[88%] sm:max-w-md rounded-3xl border border-teal-100 bg-white/82 backdrop-blur px-6 py-5 sm:px-7 sm:py-6 shadow-xl">
              <p className="text-sm uppercase tracking-wide text-teal-600 mb-2">Client experience</p>
              <h3 className="text-2xl sm:text-3xl font-bold mb-3">For Clients</h3>
              <ul className="space-y-2 text-teal-800 text-sm sm:text-base">
                <li>• Private journaling</li>
                <li>• Mood & emotion tracking</li>
                <li>• Voice & video reflections</li>
                <li>• Upload photos & files</li>
                <li>• Choose exactly what to share</li>
              </ul>
            </div>
          </motion.div>

          {/* For Therapists: text left, dashboard right */}
          <motion.div
            initial={{ x: 30, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl shadow-2xl bg-gradient-to-br from-[#F1F8F7] to-[#E0F2F1] min-h-[360px] md:min-h-[520px] p-4 sm:p-5"
          >
            <div className="rounded-3xl overflow-hidden">
              <img
                src="/therapist_dashboard.png"
                alt="Therapist dashboard — client overview, trends, and notes"
                className="w-full h-full max-h-[620px] object-contain"
              />
            </div>
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-0 md:right-0 md:translate-x-1/3 -translate-y-1/4 max-w-[88%] sm:max-w-md rounded-3xl border border-teal-100 bg-white/82 backdrop-blur px-6 py-5 sm:px-7 sm:py-6 shadow-xl">
              <p className="text-sm uppercase tracking-wide text-teal-600 mb-2">Therapist view</p>
              <h3 className="text-2xl sm:text-3xl font-bold mb-3">For Therapists</h3>
              <ul className="space-y-2 text-teal-800 text-sm sm:text-base">
                <li>• Client dashboard</li>
                <li>• Emotional trend graphs</li>
                <li>• Search by mood or content</li>
                <li>• Private therapist notes</li>
                <li>• Clear activity status</li>
                <li>• Read shared journal entries</li>
                <li>• Manage Clients</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Animated Graph */}
      <section className="py-16 sm:py-24 bg-white/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8"
          >
            EmoGraph : Insight At A Glance
          </motion.h2>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="rounded-3xl overflow-hidden shadow-2xl"
          >
            <div className="relative bg-gradient-to-br from-[#0f1629] via-[#131b3a] to-[#1a1145] p-6 sm:p-10 md:p-12">
              {/* Grid lines */}
              <div className="absolute inset-0 opacity-[0.07]" style={{
                backgroundImage: "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)",
                backgroundSize: "60px 40px"
              }} />
              {/* Animated EmoGraph — May demo */}
              <EmoGraphAnimation />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12"
          >
            What Therapists and Journalers Are Saying
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white/90 p-6 sm:p-8 rounded-3xl shadow-md border border-teal-100"
            >
              <div className="text-xs font-semibold uppercase tracking-wide text-teal-600 mb-3">Therapist</div>
              <p className="text-teal-800 text-sm sm:text-base leading-relaxed">"Clou transformed how I prepare for sessions. I now see emotional patterns before clients arrive, and sessions are deeper from the first minute."</p>
              <div className="mt-4 font-semibold text-teal-700 text-sm sm:text-base">Dr. Emma Reid, CBT Therapist</div>
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/90 p-6 sm:p-8 rounded-3xl shadow-md border border-teal-100"
            >
              <div className="text-xs font-semibold uppercase tracking-wide text-teal-600 mb-3">Therapist</div>
              <p className="text-teal-800 text-sm sm:text-base leading-relaxed">"My clients feel supported between sessions without increasing my workload. It keeps boundaries clear and insight strong."</p>
              <div className="mt-4 font-semibold text-teal-700 text-sm sm:text-base">Michael Harris, Psychotherapist</div>
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white/90 p-6 sm:p-8 rounded-3xl shadow-md border border-teal-100"
            >
              <div className="text-xs font-semibold uppercase tracking-wide text-teal-600 mb-3">Client Journaler</div>
              <p className="text-teal-800 text-sm sm:text-base leading-relaxed">"Journaling in Clou helps me organize my thoughts between sessions. My therapist understands where I am emotionally before we even start, and I feel more supported week to week."</p>
              <div className="mt-4 font-semibold text-teal-700 text-sm sm:text-base">Sophie Lane, Client Journaler</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-white/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3"
          >
            Simple Plans for Therapists
          </motion.h2>
          <p className="text-teal-700 text-base sm:text-lg mb-8 sm:mb-12">Always free for Clients</p>
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="border border-teal-200 rounded-3xl p-6 sm:p-10 shadow-sm bg-white/80"
            >
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 text-teal-800">Core Plan</h3>
              <ul className="text-teal-800 mb-4 text-sm sm:text-base space-y-2 text-left mx-auto max-w-md">
                <li>• Up to 3 active clients</li>
                <li>• Secure journaling</li>
                <li>• Basic emotion tracking</li>
                <li>• Private therapist notes</li>
                <li>• Public profile</li>
              </ul>
              <p className="text-2xl sm:text-3xl font-bold text-teal-700">Free</p>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="border-2 border-teal-400 rounded-3xl p-6 sm:p-10 shadow-lg relative bg-white/90"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-400 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-medium">Most Popular</div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 text-teal-800">Professional Plan</h3>
              <ul className="text-teal-800 mb-4 text-sm sm:text-base space-y-2 text-left mx-auto max-w-md">
                <li>• Unlimited clients</li>
                <li>• Advanced analytics</li>
                <li>• Full Calendly & Google Calendar integration</li>
                <li>• Verified badge</li>
                <li>• Priority support</li>
              </ul>
              <p className="text-2xl sm:text-3xl font-bold text-teal-700">€29 / month</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-[#B2DFDB] to-[#80CBC4] text-[#034D4D] py-16 sm:py-24 text-center">
        <motion.h2
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-2xl sm:text-3xl md:text-4xl font-bold px-4"
        >
          Better sessions start before the session.
        </motion.h2>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-6 flex justify-center gap-3 sm:gap-4 flex-wrap px-4"
        >
          <button
            onClick={() => navigate("/therapist-login")}
            className="bg-teal-400 text-white px-5 py-3 sm:px-6 sm:py-3 rounded-2xl font-semibold hover:scale-105 transition duration-300 flex items-center gap-2"
          >
            Join as Therapist <ArrowRight className="inline" size={16} />
          </button>
          <button
            onClick={() => navigate("/client-login")}
            className="bg-white/10 border border-white/30 px-5 py-3 sm:px-6 sm:py-3 rounded-2xl font-semibold hover:bg-white/20 transition"
          >
            Join as Client
          </button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="text-center py-8 sm:py-12 text-sm text-teal-800 bg-white/50">
        © 2026 Clou. Secure between-session therapy support.
      </footer>
    </main>
  );
};

export default Landing;
